import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import {
  BizDevelopmentDashboardEndPointLogic,
  OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES,
  OverviewAccountSummaryTimeframe,
} from "../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import {
  setComparisonQueryOutput,
  setComparisonFetchedFor,
  setIsLoading,
} from "../../../../features/bizDevelopmentDashboardSlice";
import store from "../../../../store";
import { BIZ_DEVELOPMENT_BUDGET_QUERY_ID } from "../QueryIDsBizDevelopmentDashboard";
import {
  setIsFetchedForBrandData,
  setIsFetchedForEarningData,
  setIsFetchedForFtdData,
  setIsFetchedForIncomeData,
} from "../../../../features/bizDevelopmentAnomaliesSlice";
import dayjs from "dayjs";

export const useBizDevelopmentDashboard = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = userData?.data?.oauth_token;

  const type = "site"; // statically set as per current requirements
  const dimension = ["bbb_uk"]; // statically set as per current requirements
  const queryId: string = BIZ_DEVELOPMENT_BUDGET_QUERY_ID;

  const getSitesFromParams = (): string[] => {
    const sites = searchParams.get("sites");
    return sites ? sites.split(",").filter(Boolean) : [];
  };

  const getTimeframeFromParams = (): string => {
    return searchParams.get("timeframePerformance") || "yesterday";
  };

  const getOverviewAccountSummaryTimeframeFromParams =
    (): OverviewAccountSummaryTimeframe => {
      return (searchParams.get("timeframeOverviewAccountSummary") ||
        OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastThirtyDays) as OverviewAccountSummaryTimeframe;
    };

  const fetchDashboardData = async (
    timeframe?: string,
    siteSharedUids?: string[],
    customRange?: { startDate: string; endDate: string },
  ) => {
    const resolvedTimeframe = timeframe ?? getTimeframeFromParams();
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    await BizDevelopmentDashboardEndPointLogic.getDashboardData(
      oauthTokenHeader,
      resolvedSites,
      resolvedTimeframe,
      customRange,
    );
  };

  const fetchComparisonData = async (
    timeframe: string,
    siteSharedUids?: string[],
    customRange?: { startDate: string; endDate: string },
  ) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    await BizDevelopmentDashboardEndPointLogic.getComparisonDashboardData(
      oauthTokenHeader,
      resolvedSites,
      timeframe,
      customRange,
    );
  };

  const clearComparisonData = () => {
    store.dispatch(setComparisonQueryOutput([]));
    store.dispatch(setComparisonFetchedFor(null));
  };

  const fetchBudgetData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getBizDevelopmentBudgetData({
      oauthTokenHeader,
      queryId,
      websiteSharedUids: resolvedSites,
      type,
      dimension,
    });
  };

  const fetchOverviewAccountSummaryData = async (
    timeframe?: OverviewAccountSummaryTimeframe,
    siteSharedUids?: string[],
  ) => {
    const resolvedTimeframe =
      timeframe ?? getOverviewAccountSummaryTimeframeFromParams();
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getOverviewAccountSummaryData(
      oauthTokenHeader,
      resolvedSites,
      resolvedTimeframe,
    );
  };

  const fetchBeAwareFraudPlayersData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getBeAwareFraudPlayersData(
      oauthTokenHeader,
      resolvedSites,
    );
  };

  const fetchBeAwareNegativeBalanceData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getBeAwareNegativeBalanceData(
      oauthTokenHeader,
      resolvedSites,
    );
  };

  const fetchBeAwareConversionsUploadData = async (
    siteSharedUids?: string[],
  ) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getBeAwareConversionsUploadData(
      oauthTokenHeader,
      resolvedSites,
    );
  };

  //Fetch Anomalies Missing Data
  const fetchMissingData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getAnomaliesMissingData({
      oauthTokenHeader,
      websiteSharedUids: resolvedSites,
      type,
      dimension,
    });
  };

  // Fetch Anomalies FTD Data - Performance + Problematic
  const fetchFtdData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    try {
      dispatch(setIsLoading(true));
      dispatch(setIsFetchedForFtdData(false));
      await Promise.all([
        BizDevelopmentDashboardEndPointLogic.getAnomaliesPerformanceFtdData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
        BizDevelopmentDashboardEndPointLogic.getAnomaliesProblematicFtdData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
      ]);
    } finally {
      dispatch(setIsLoading(false));
      dispatch(setIsFetchedForFtdData(true));
    }
  };

  // Fetch Anomalies Brand Tab Data - Without FTD & Net Rev Brand + Gaps Between Players And Daily Performance
  const fetchAnomaliesBrandData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    // Get data for last 60 days: start = today - 59 days, end = today. as per requirements
    // Subtract 59 days so the date range includes today, making a total of 60 days.
    const startDate = dayjs().subtract(59, "day").format("YYYY-MM-DD"); // today − 59
    const endDate = dayjs().format("YYYY-MM-DD");

    try {
      dispatch(setIsLoading(true));
      dispatch(setIsFetchedForBrandData(false));
      await Promise.allSettled([
        BizDevelopmentDashboardEndPointLogic.getWithoutFtdAndNetRevBrandData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
        BizDevelopmentDashboardEndPointLogic.getGapsBetweenPlayersAndDailyPerformance(
          {
            oauthTokenHeader,
            websiteSharedUids: resolvedSites,
            type,
            dimension,
            startDate,
            endDate,
          },
        ),
      ]);
    } finally {
      dispatch(setIsLoading(false));
      dispatch(setIsFetchedForBrandData(true));
    }
  };

  // Fetch Anomalies Income Tab Data - Income gaps in MTD + Decrease in daily Income gaps in MTDs
  const fetchAnomaliesIncomeData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    try {
      dispatch(setIsLoading(true));
      dispatch(setIsFetchedForIncomeData(false));
      await Promise.all([
        BizDevelopmentDashboardEndPointLogic.getIncomeGapsInMTDData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
        BizDevelopmentDashboardEndPointLogic.getDecreaseDailyIncomeGapsMTDData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
      ]);
    } finally {
      dispatch(setIsLoading(false));
      dispatch(setIsFetchedForIncomeData(true));
    }
  };

  // Fetch Anomalies Earnings Tab Data - Daily Earning Gaps in MTD + Monthly Earning Gaps in MTD
  const fetchAnomaliesEarningsData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    try {
      dispatch(setIsLoading(true));
      dispatch(setIsFetchedForEarningData(false));
      await Promise.all([
        BizDevelopmentDashboardEndPointLogic.getDailyEarningGapsMTDData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
        BizDevelopmentDashboardEndPointLogic.getMonthlyEarningGapsMTDData({
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type,
          dimension,
        }),
      ]);
    } finally {
      dispatch(setIsLoading(false));
      dispatch(setIsFetchedForEarningData(true));
    }
  };

  return {
    fetchDashboardData,
    fetchComparisonData,
    fetchOverviewAccountSummaryData,
    clearComparisonData,
    getSitesFromParams,
    getTimeframeFromParams,
    fetchBudgetData,
    getOverviewAccountSummaryTimeframeFromParams,
    fetchBeAwareFraudPlayersData,
    fetchBeAwareNegativeBalanceData,
    fetchBeAwareConversionsUploadData,
    fetchMissingData,
    fetchFtdData,
    fetchAnomaliesBrandData,
    fetchAnomaliesIncomeData,
    fetchAnomaliesEarningsData,
  };
};
